import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../App';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { FileDown, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReportsPage = () => {
  const { sessionToken } = useAuth();
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedDay, setSelectedDay] = useState(new Date().getDate());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [statusFilter, setStatusFilter] = useState('all');
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);

  const days = Array.from({ length: 31 }, (_, i) => i + 1);
  const months = [
    { value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' },
    { value: 4, label: 'April' }, { value: 5, label: 'May' }, { value: 6, label: 'June' },
    { value: 7, label: 'July' }, { value: 8, label: 'August' }, { value: 9, label: 'September' },
    { value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' }
  ];

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

  useEffect(() => {
    fetchClasses();
  }, []);

  useEffect(() => {
    if (selectedClass) {
      fetchReport();
    }
  }, [selectedClass, selectedDay, selectedMonth, selectedYear, statusFilter]);

  const fetchClasses = async () => {
    try {
      const response = await axios.get(`${API}/classes`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      setClasses(response.data);
      setSelectedClass('all');
    } catch (error) {
      console.error('Failed to fetch classes:', error);
      toast.error('Failed to load classes');
    }
  };

  const fetchReport = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/reports/attendance?class_id=${selectedClass}&day=${selectedDay}&month=${selectedMonth}&year=${selectedYear}&status=${statusFilter}`,
        { headers: { Authorization: `Bearer ${sessionToken}` } }
      );
      setReportData(response.data);
    } catch (error) {
      console.error('Failed to fetch report:', error);
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = async () => {
    try {
      const response = await axios.get(
        `${API}/reports/export/pdf?class_id=${selectedClass}&day=${selectedDay}&month=${selectedMonth}&year=${selectedYear}&status=${statusFilter}`,
        { headers: { Authorization: `Bearer ${sessionToken}` }, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `attendance_report_${selectedDay}_${selectedMonth}_${selectedYear}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF downloaded successfully!');
    } catch (error) {
      console.error('Failed to download PDF:', error);
      toast.error('Failed to download PDF');
    }
  };

  const downloadExcel = async () => {
    try {
      const response = await axios.get(
        `${API}/reports/export/excel?class_id=${selectedClass}&day=${selectedDay}&month=${selectedMonth}&year=${selectedYear}&status=${statusFilter}`,
        { headers: { Authorization: `Bearer ${sessionToken}` }, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `attendance_report_${selectedDay}_${selectedMonth}_${selectedYear}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Excel downloaded successfully!');
    } catch (error) {
      console.error('Failed to download Excel:', error);
      toast.error('Failed to download Excel');
    }
  };

  const sendToWhatsApp = () => {
    if (reportData.length === 0) {
      toast.error('No data to send');
      return;
    }

    const monthName = months.find(m => m.value === selectedMonth)?.label || selectedMonth;
    const statusText = statusFilter !== 'all' ? ` (${statusFilter.toUpperCase()} only)` : '';
    let message = `*Attendance Report${statusText}*\nDate: ${selectedDay} ${monthName} ${selectedYear}\n\n`;

    reportData.forEach((record) => {
      message += `*${record.student_name}* - Roll: ${record.roll_no}\n`;
      if (selectedClass === 'all') {
        message += `Class: ${record.class_name}\n`;
      }
      message += `Status: ${record.status || 'N/A'}\n\n`;
    });

    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/?text=${encodedMessage}`, '_blank');
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-slate-900 font-heading mb-2" data-testid="reports-title">Attendance Reports</h1>
          <p className="text-slate-500 text-lg">View and export attendance reports</p>
        </div>

        <Card className="p-6 rounded-2xl border-slate-100 bg-white mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">Class</label>
              <Select value={selectedClass} onValueChange={setSelectedClass}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder="Select class" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">ALL CLASSES</SelectItem>
                  {classes.map((cls) => (
                    <SelectItem key={cls._id} value={cls._id}>{cls.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">Day</label>
              <Select value={selectedDay.toString()} onValueChange={(v) => setSelectedDay(parseInt(v))}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {days.map((d) => (
                    <SelectItem key={d} value={d.toString()}>{d}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">Month</label>
              <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {months.map((m) => (
                    <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">Year</label>
              <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {years.map((y) => (
                    <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex gap-3 flex-wrap">
            <div className="flex gap-2 flex-wrap items-center">
              <span className="text-sm font-medium text-slate-700">Filter:</span>
              <Button onClick={() => setStatusFilter('all')} className={`${statusFilter === 'all' ? 'bg-sky-500 text-white' : 'bg-slate-200 text-slate-700'} rounded-full px-4 py-2 text-sm`}>All</Button>
              <Button onClick={() => setStatusFilter('present')} className={`${statusFilter === 'present' ? 'bg-green-500 text-white' : 'bg-slate-200 text-slate-700'} rounded-full px-4 py-2 text-sm`}>Present</Button>
              <Button onClick={() => setStatusFilter('absent')} className={`${statusFilter === 'absent' ? 'bg-red-500 text-white' : 'bg-slate-200 text-slate-700'} rounded-full px-4 py-2 text-sm`}>Absent</Button>
              <Button onClick={() => setStatusFilter('late')} className={`${statusFilter === 'late' ? 'bg-amber-500 text-white' : 'bg-slate-200 text-slate-700'} rounded-full px-4 py-2 text-sm`}>Late</Button>
              <Button onClick={() => setStatusFilter('homework')} className={`${statusFilter === 'homework' ? 'bg-purple-500 text-white' : 'bg-slate-200 text-slate-700'} rounded-full px-4 py-2 text-sm`}>Homework Not Done</Button>
            </div>
            <div className="flex gap-3 ml-auto">
              <Button onClick={downloadPDF} className="bg-red-500 hover:bg-red-600 text-white rounded-full">
                <FileDown className="w-5 h-5 mr-2" />PDF
              </Button>
              <Button onClick={downloadExcel} className="bg-green-500 hover:bg-green-600 text-white rounded-full">
                <FileSpreadsheet className="w-5 h-5 mr-2" />Excel
              </Button>
              <Button onClick={sendToWhatsApp} className="bg-green-600 hover:bg-green-700 text-white rounded-full">
                WhatsApp
              </Button>
            </div>
          </div>
        </Card>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-sky-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-slate-600 font-medium">Loading report...</p>
            </div>
          </div>
        ) : reportData.length === 0 ? (
          <Card className="p-8 rounded-2xl border-slate-100 bg-white text-center">
            <p className="text-slate-500">No attendance data found for the selected period.</p>
          </Card>
        ) : (
          <Card className="rounded-2xl border-slate-100 bg-white overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-sky-50">
                  <TableHead>Roll No</TableHead>
                  <TableHead>Student Name</TableHead>
                  {selectedClass === 'all' && <TableHead>Class</TableHead>}
                  <TableHead className="text-center">Present</TableHead>
                  <TableHead className="text-center">Absent</TableHead>
                  <TableHead className="text-center">Late</TableHead>
                  <TableHead className="text-center">Homework Not Done</TableHead>
                  <TableHead className="text-center">Attendance %</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {reportData.map((record, index) => (
                  <TableRow key={index} data-testid={`report-row-${index}`}>
                    <TableCell className="font-medium">{record.roll_no}</TableCell>
                    <TableCell>{record.student_name}</TableCell>
                    {selectedClass === 'all' && <TableCell>{record.class_name}</TableCell>}
                    <TableCell className="text-center">
                      <span className="inline-flex items-center justify-center w-8 h-8 bg-green-100 text-green-700 rounded-full text-sm font-semibold">
                        {record.present}
                      </span>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="inline-flex items-center justify-center w-8 h-8 bg-red-100 text-red-700 rounded-full text-sm font-semibold">
                        {record.absent}
                      </span>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="inline-flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold">
                        {record.late}
                      </span>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="inline-flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold">
                        {record.homework}
                      </span>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className={`font-semibold text-lg ${
                        record.attendance_percentage >= 90 ? 'text-green-600' :
                        record.attendance_percentage >= 75 ? 'text-blue-600' :
                        'text-red-600'
                      }`}>
                        {record.attendance_percentage}%
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default ReportsPage;